/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * CampaignComposerView — multi-step wizard for creating a new
 * SMS / voter messaging campaign.
 *
 * Role wall: CAMPAIGN_MANAGER, MANAGER, ORG_MANAGER, ADMIN.
 *
 * Steps:
 *   1. Basics    — name, mode (batch/p2p), schedule (now / scheduled)
 *   2. Template  — message body + merge-field pills + SMS segment counter
 *   3. Audience  — AudienceQuery builder (precincts, party score, limit)
 *   4. Preview   — calls previewBatch and shows eligibility breakdown
 *   5. Attest    — TCPA attestation checkbox + launch
 *
 * The eligibility filters (has_cell_phone, do_not_text_eq=false,
 * sms_opt_in_required=true) are forced ON in batch mode — they're a
 * compliance floor, not a UI toggle. P2P mode is allowed through Step 1
 * but the launch flow short-circuits with a note pointing the user at
 * the click-by-click Send Texts view (which is out of scope for this
 * component).
 *
 * Launch path:
 *   sendBatch({ name, template, audience_query, scheduled_at, consent_attested: true })
 *   → onNavigate('campaign-detail', { campaignId: res.campaign_id })
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Check,
  CheckCircle2,
  Loader2,
  Megaphone,
  Send,
  Users,
} from 'lucide-react';
import { previewBatch, sendBatch } from '@/api/messaging';
import type {
  AudienceQuery,
  BatchPreviewResponse,
  VoterCampaignMode,
} from '@/types/messaging';
import { ViewHeader } from '@/components/ViewHeader';
import { FilterMultiSelect } from '@/components/filters';
import { roleMatches } from '@/lib/roles';

// ---------------------------------------------------------------------------
// Role gate
// ---------------------------------------------------------------------------

const ALLOWED_ROLES = new Set([
  'SMS_MANAGER',
  'MANAGER',
  'ORG_MANAGER',
  'ADMIN',
]);

function isAllowed(userRole: string | undefined): boolean {
  if (!userRole) return false;
  return roleMatches(userRole, ALLOWED_ROLES);
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const PARTY_SCORE_OPTIONS = [
  { value: '1', label: 'Hard R (1)' },
  { value: '2', label: 'Weak R (2)' },
  { value: '3', label: 'Swing (3)' },
  { value: '4', label: 'Weak D (4)' },
  { value: '5', label: 'Hard D (5)' },
];

// Platte County precinct numbers. Limited known list — augment later
// once a /precincts endpoint exists. This intentionally avoids making
// a new API call from a composer view.
const PRECINCT_FALLBACK = Array.from({ length: 40 }, (_, i) => {
  const n = String(i + 1).padStart(3, '0');
  return { value: n, label: `Precinct ${n}` };
});

const MERGE_FIELDS = [
  '{first_name}',
  '{last_name}',
  '{org_name}',
  '{volunteer_first_name}',
];

const NAME_MAX = 120;

// Single SMS segment = 160 GSM chars. Beyond that, segments are 153 chars
// each because of UDH. We don't try to detect non-GSM (Unicode) here —
// that's the backend's problem — but we surface the warning at >1 segment.
function segmentsFor(text: string): number {
  if (text.length === 0) return 0;
  if (text.length <= 160) return 1;
  return Math.ceil(text.length / 153);
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface CampaignComposerViewProps {
  userRole?: string;
  currentUser?: { id: number; full_name?: string };
  onNavigate?: (view: string, params?: Record<string, unknown>) => void;
  onCancel?: () => void;
}

type Step = 1 | 2 | 3 | 4 | 5;

export function CampaignComposerView({
  userRole,
  currentUser: _currentUser,
  onNavigate,
  onCancel,
}: CampaignComposerViewProps) {
  if (!isAllowed(userRole)) {
    return (
      <div className="min-h-dvh bg-bg-base text-fg-primary p-6">
        <div className="max-w-2xl mx-auto rounded-lg border border-status-danger-bg/30 bg-status-danger-bg/20 p-6">
          <h2 className="text-lg font-semibold text-status-danger mb-2">
            Access Denied
          </h2>
          <p className="text-fg-secondary">
            Creating campaigns is restricted to campaign managers and above.
          </p>
        </div>
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // Form state
  // -------------------------------------------------------------------------
  const [step, setStep] = useState<Step>(1);

  // Step 1: basics
  const [name, setName] = useState('');
  const [mode, setMode] = useState<VoterCampaignMode>('batch');
  const [scheduleKind, setScheduleKind] = useState<'now' | 'later'>('now');
  const [scheduledAt, setScheduledAt] = useState<string>('');

  // Step 2: template
  const [template, setTemplate] = useState('');
  const templateRef = useRef<HTMLTextAreaElement | null>(null);

  // Step 3: audience
  const [precinctValues, setPrecinctValues] = useState<string[]>([]);
  const [partyScoreValues, setPartyScoreValues] = useState<string[]>([]);
  const [audienceLimit, setAudienceLimit] = useState<number>(1000);

  // Step 4: preview state
  const [preview, setPreview] = useState<BatchPreviewResponse | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  // Step 5: attest + launch
  const [consentAttested, setConsentAttested] = useState(false);
  const [launching, setLaunching] = useState(false);
  const [launchError, setLaunchError] = useState<string | null>(null);

  // -------------------------------------------------------------------------
  // Derived
  // -------------------------------------------------------------------------
  const audienceQuery = useMemo<AudienceQuery>(() => {
    const q: AudienceQuery = {
      // Compliance floor — forced ON for batch.
      has_cell_phone: true,
      do_not_text_eq: false,
      sms_opt_in_required: true,
    };
    if (precinctValues.length > 0) q.precinct_num = precinctValues;
    if (partyScoreValues.length > 0) {
      q.calculated_party_score_in = partyScoreValues.map((v) =>
        Number.parseInt(v, 10),
      );
    }
    if (audienceLimit > 0) q.limit = audienceLimit;
    return q;
  }, [precinctValues, partyScoreValues, audienceLimit]);

  const templateSegments = useMemo(() => segmentsFor(template), [template]);
  const templateHasStop = useMemo(
    () => /\bstop\b/i.test(template),
    [template],
  );

  // -------------------------------------------------------------------------
  // Validation per step
  // -------------------------------------------------------------------------
  const canAdvance = useMemo(() => {
    switch (step) {
      case 1:
        if (!name.trim()) return false;
        if (name.length > NAME_MAX) return false;
        if (scheduleKind === 'later' && !scheduledAt) return false;
        return true;
      case 2:
        return template.trim().length > 0;
      case 3:
        return true; // audience can be empty (means full opted-in roll)
      case 4:
        return preview !== null && preview.eligible_to_send > 0;
      case 5:
        return consentAttested && !launching;
      default:
        return false;
    }
  }, [
    step,
    name,
    scheduleKind,
    scheduledAt,
    template,
    preview,
    consentAttested,
    launching,
  ]);

  // -------------------------------------------------------------------------
  // Effects — fetch preview when entering Step 4
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (step !== 4) return;
    let cancelled = false;
    setPreviewLoading(true);
    setPreviewError(null);
    setPreview(null);

    previewBatch({ audience_query: audienceQuery, template })
      .then((res) => {
        if (cancelled) return;
        setPreview(res);
        setPreviewLoading(false);
      })
      .catch((e: any) => {
        if (cancelled) return;
        const status = e?.response?.status;
        if (status === 403) {
          setPreviewError('Your role does not have access to preview.');
        } else if (status) {
          setPreviewError(`Backend error ${status} while previewing.`);
        } else {
          setPreviewError('Could not reach the messaging backend.');
        }
        setPreviewLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [step, audienceQuery, template]);

  // -------------------------------------------------------------------------
  // Handlers
  // -------------------------------------------------------------------------
  const insertMergeField = useCallback((field: string) => {
    const ta = templateRef.current;
    if (!ta) {
      setTemplate((t) => t + field);
      return;
    }
    const start = ta.selectionStart ?? template.length;
    const end = ta.selectionEnd ?? template.length;
    const next = template.slice(0, start) + field + template.slice(end);
    setTemplate(next);
    // Restore cursor after the inserted field on next tick.
    requestAnimationFrame(() => {
      ta.focus();
      const caret = start + field.length;
      ta.setSelectionRange(caret, caret);
    });
  }, [template]);

  const goNext = useCallback(() => {
    if (step < 5) {
      setStep((s) => ((s + 1) as Step));
    }
  }, [step]);

  const goPrev = useCallback(() => {
    if (step > 1) {
      setStep((s) => ((s - 1) as Step));
    }
  }, [step]);

  const goToStep = useCallback((s: Step) => {
    setStep(s);
  }, []);

  const handleLaunch = useCallback(async () => {
    setLaunching(true);
    setLaunchError(null);
    try {
      const res = await sendBatch({
        name: name.trim(),
        template,
        audience_query: audienceQuery,
        scheduled_at:
          scheduleKind === 'later' && scheduledAt
            ? new Date(scheduledAt).toISOString()
            : null,
        consent_attested: true,
      });
      if (onNavigate) {
        onNavigate('campaign-detail', { campaignId: res.campaign_id });
      } else if (onCancel) {
        onCancel();
      }
    } catch (e: any) {
      const status = e?.response?.status;
      const detail =
        e?.response?.data?.detail ||
        e?.message ||
        'Could not launch the campaign.';
      setLaunchError(status ? `Error ${status}: ${detail}` : detail);
      setLaunching(false);
    }
  }, [
    name,
    template,
    audienceQuery,
    scheduleKind,
    scheduledAt,
    onNavigate,
    onCancel,
  ]);

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  return (
    <div className="space-y-6">
      <ViewHeader
        title={
          <span className="flex items-center gap-2">
            <Megaphone className="w-6 h-6 text-accent" />
            New campaign
          </span>
        }
        tagline="Compose a batch SMS or P2P campaign"
        actions={
          onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="text-fg-muted hover:text-fg-primary text-sm font-semibold"
            >
              Cancel
            </button>
          )
        }
      />

      <StepIndicator step={step} onGoTo={goToStep} />

      <div className="bg-bg-raised rounded-xl p-6 border border-border-base space-y-6">
        {step === 1 && (
          <StepBasics
            name={name}
            onNameChange={setName}
            mode={mode}
            onModeChange={setMode}
            scheduleKind={scheduleKind}
            onScheduleKindChange={setScheduleKind}
            scheduledAt={scheduledAt}
            onScheduledAtChange={setScheduledAt}
          />
        )}

        {step === 2 && (
          <StepTemplate
            template={template}
            onTemplateChange={setTemplate}
            templateRef={templateRef}
            onInsertField={insertMergeField}
            segments={templateSegments}
            hasStop={templateHasStop}
          />
        )}

        {step === 3 && (
          <StepAudience
            precinctValues={precinctValues}
            onPrecinctValuesChange={setPrecinctValues}
            partyScoreValues={partyScoreValues}
            onPartyScoreValuesChange={setPartyScoreValues}
            audienceLimit={audienceLimit}
            onAudienceLimitChange={setAudienceLimit}
            mode={mode}
          />
        )}

        {step === 4 && (
          <StepPreview
            loading={previewLoading}
            error={previewError}
            preview={preview}
            onEditBasics={() => goToStep(1)}
            onEditTemplate={() => goToStep(2)}
            onEditAudience={() => goToStep(3)}
          />
        )}

        {step === 5 && (
          <StepAttest
            orgName="your organization"
            consentAttested={consentAttested}
            onConsentChange={setConsentAttested}
            launching={launching}
            error={launchError}
            onLaunch={handleLaunch}
            eligibleCount={preview?.eligible_to_send ?? 0}
            scheduleKind={scheduleKind}
            scheduledAt={scheduledAt}
            mode={mode}
          />
        )}
      </div>

      {/* Footer nav */}
      <div className="flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={goPrev}
          disabled={step === 1 || launching}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded bg-bg-elevated hover:bg-border-subtle disabled:opacity-40 disabled:cursor-not-allowed text-fg-primary text-sm font-semibold"
        >
          <ArrowLeft className="w-4 h-4" aria-hidden="true" />
          Back
        </button>

        {step < 5 ? (
          <button
            type="button"
            onClick={goNext}
            disabled={!canAdvance}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded bg-accent hover:bg-accent-hover disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-semibold"
          >
            Next
            <ArrowRight className="w-4 h-4" aria-hidden="true" />
          </button>
        ) : (
          <button
            type="button"
            onClick={handleLaunch}
            disabled={!canAdvance}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded bg-status-success-bg hover:bg-status-success-bg/80 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-bold"
          >
            {launching ? (
              <>
                <Loader2
                  className="w-4 h-4 animate-spin"
                  aria-hidden="true"
                />
                Launching…
              </>
            ) : (
              <>
                <Send className="w-4 h-4" aria-hidden="true" />
                Launch Campaign
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function StepIndicator({
  step,
  onGoTo,
}: {
  step: Step;
  onGoTo: (s: Step) => void;
}) {
  const labels: Array<{ n: Step; label: string }> = [
    { n: 1, label: 'Basics' },
    { n: 2, label: 'Template' },
    { n: 3, label: 'Audience' },
    { n: 4, label: 'Preview' },
    { n: 5, label: 'Attest & launch' },
  ];
  return (
    <div className="bg-bg-raised rounded-xl p-3 border border-border-base">
      <ol className="flex items-center justify-between gap-2 flex-wrap">
        {labels.map((l, i) => {
          const active = step === l.n;
          const past = step > l.n;
          return (
            <React.Fragment key={l.n}>
              <li className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => (past ? onGoTo(l.n) : undefined)}
                  disabled={!past}
                  className={`w-7 h-7 inline-flex items-center justify-center rounded-full text-xs font-bold transition-colors ${
                    active
                      ? 'bg-accent text-white'
                      : past
                        ? 'bg-status-success-bg text-white hover:opacity-80 cursor-pointer'
                        : 'bg-bg-elevated text-fg-muted'
                  }`}
                  title={past ? `Jump back to ${l.label}` : l.label}
                >
                  {past ? (
                    <Check className="w-3.5 h-3.5" aria-hidden="true" />
                  ) : (
                    l.n
                  )}
                </button>
                <span
                  className={`text-sm font-semibold ${
                    active
                      ? 'text-fg-primary'
                      : past
                        ? 'text-fg-secondary'
                        : 'text-fg-muted'
                  }`}
                >
                  {l.label}
                </span>
              </li>
              {i < labels.length - 1 && (
                <li
                  aria-hidden="true"
                  className={`h-px flex-1 ${past ? 'bg-status-success-bg' : 'bg-border-base'}`}
                />
              )}
            </React.Fragment>
          );
        })}
      </ol>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 1 — Basics
// ---------------------------------------------------------------------------
function StepBasics({
  name,
  onNameChange,
  mode,
  onModeChange,
  scheduleKind,
  onScheduleKindChange,
  scheduledAt,
  onScheduledAtChange,
}: {
  name: string;
  onNameChange: (v: string) => void;
  mode: VoterCampaignMode;
  onModeChange: (m: VoterCampaignMode) => void;
  scheduleKind: 'now' | 'later';
  onScheduleKindChange: (k: 'now' | 'later') => void;
  scheduledAt: string;
  onScheduledAtChange: (v: string) => void;
}) {
  return (
    <div className="space-y-5">
      <h2 className="text-lg font-bold text-fg-primary">Basics</h2>

      <label className="flex flex-col gap-1">
        <span className="text-xs uppercase tracking-wide text-fg-muted font-semibold">
          Campaign name <span className="text-status-danger">*</span>
        </span>
        <input
          type="text"
          value={name}
          maxLength={NAME_MAX}
          onChange={(e) => onNameChange(e.target.value)}
          placeholder="e.g. November GOTV — Hard R, west precincts"
          className="bg-bg-base border border-border-base rounded px-3 py-2 text-fg-primary placeholder-fg-muted text-sm"
        />
        <span className="text-[11px] text-fg-muted">
          {name.length} / {NAME_MAX}
        </span>
      </label>

      <div className="flex flex-col gap-2">
        <span className="text-xs uppercase tracking-wide text-fg-muted font-semibold">
          Mode
        </span>
        <div className="flex items-center gap-2">
          <ModeButton
            label="Batch"
            description="Send to many at once"
            active={mode === 'batch'}
            onClick={() => onModeChange('batch')}
          />
          <ModeButton
            label="P2P"
            description="One-by-one click-to-send"
            active={mode === 'p2p'}
            onClick={() => onModeChange('p2p')}
          />
        </div>
        {mode === 'p2p' && (
          <div className="text-xs text-status-info bg-status-info-bg/20 border border-status-info-bg/40 rounded p-3">
            P2P campaigns are sent click-by-click in the Send Texts view —
            see your queue there.
          </div>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <span className="text-xs uppercase tracking-wide text-fg-muted font-semibold">
          Schedule
        </span>
        <div className="flex items-center gap-3">
          <label className="inline-flex items-center gap-2 text-sm text-fg-secondary">
            <input
              type="radio"
              name="schedule"
              checked={scheduleKind === 'now'}
              onChange={() => onScheduleKindChange('now')}
              className="accent-emerald-500"
            />
            Send immediately
          </label>
          <label className="inline-flex items-center gap-2 text-sm text-fg-secondary">
            <input
              type="radio"
              name="schedule"
              checked={scheduleKind === 'later'}
              onChange={() => onScheduleKindChange('later')}
              className="accent-emerald-500"
            />
            Schedule for…
          </label>
        </div>
        {scheduleKind === 'later' && (
          <input
            type="datetime-local"
            value={scheduledAt}
            onChange={(e) => onScheduledAtChange(e.target.value)}
            className="bg-bg-base border border-border-base rounded px-3 py-2 text-fg-primary text-sm max-w-xs"
          />
        )}
      </div>
    </div>
  );
}

function ModeButton({
  label,
  description,
  active,
  onClick,
}: {
  label: string;
  description: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`flex-1 max-w-xs text-left rounded-lg border px-3 py-2 transition-colors ${
        active
          ? 'border-accent bg-accent/15 text-fg-primary'
          : 'border-border-base bg-bg-base text-fg-secondary hover:bg-bg-elevated'
      }`}
    >
      <div className="font-bold">{label}</div>
      <div className="text-xs text-fg-muted">{description}</div>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Step 2 — Template
// ---------------------------------------------------------------------------
function StepTemplate({
  template,
  onTemplateChange,
  templateRef,
  onInsertField,
  segments,
  hasStop,
}: {
  template: string;
  onTemplateChange: (v: string) => void;
  templateRef: React.RefObject<HTMLTextAreaElement>;
  onInsertField: (f: string) => void;
  segments: number;
  hasStop: boolean;
}) {
  return (
    <div className="space-y-5">
      <h2 className="text-lg font-bold text-fg-primary">Message template</h2>

      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4">
        <textarea
          ref={templateRef}
          value={template}
          onChange={(e) => onTemplateChange(e.target.value)}
          rows={6}
          placeholder="Hi {first_name}, this is a friendly reminder to vote on Tuesday! Reply STOP to opt out."
          className="bg-bg-base border border-border-base rounded p-3 text-fg-primary placeholder-fg-muted text-sm font-mono resize-y"
        />

        <div className="flex flex-col gap-1 min-w-[180px]">
          <span className="text-[10px] uppercase tracking-wide text-fg-muted font-semibold">
            Merge fields
          </span>
          {MERGE_FIELDS.map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => onInsertField(f)}
              className="text-left rounded border border-border-base bg-bg-base hover:bg-bg-elevated text-fg-secondary hover:text-fg-primary text-xs font-mono px-2 py-1.5"
              title={`Insert ${f} at cursor`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap text-xs">
        <span
          className={
            segments > 1 ? 'text-status-warning' : 'text-fg-muted tabular-nums'
          }
        >
          {template.length} / 160 ·{' '}
          {segments === 0
            ? '0 segments'
            : segments === 1
              ? '1 SMS segment'
              : `${segments} SMS segments`}
        </span>
        {segments > 1 && (
          <span className="text-status-warning inline-flex items-center gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5" aria-hidden="true" />
            Multi-segment messages cost more and are slower to deliver
          </span>
        )}
      </div>

      {template.trim().length > 0 && !hasStop && (
        <div className="text-xs text-status-warning bg-status-warning-bg/20 border border-status-warning-bg/40 rounded p-3 inline-flex items-start gap-2">
          <AlertTriangle
            className="w-4 h-4 flex-shrink-0 mt-0.5"
            aria-hidden="true"
          />
          <span>
            First-message templates should include &quot;Reply STOP to opt
            out.&quot;
          </span>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 3 — Audience
// ---------------------------------------------------------------------------
function StepAudience({
  precinctValues,
  onPrecinctValuesChange,
  partyScoreValues,
  onPartyScoreValuesChange,
  audienceLimit,
  onAudienceLimitChange,
  mode,
}: {
  precinctValues: string[];
  onPrecinctValuesChange: (v: string[]) => void;
  partyScoreValues: string[];
  onPartyScoreValuesChange: (v: string[]) => void;
  audienceLimit: number;
  onAudienceLimitChange: (v: number) => void;
  mode: VoterCampaignMode;
}) {
  return (
    <div className="space-y-5">
      <h2 className="text-lg font-bold text-fg-primary">Audience</h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FilterMultiSelect
          label="Precincts"
          values={precinctValues}
          options={PRECINCT_FALLBACK}
          onChange={onPrecinctValuesChange}
        />

        <div className="flex flex-col gap-2">
          <span className="text-[10px] uppercase tracking-wide text-fg-muted font-semibold">
            Calculated party score
          </span>
          <div className="flex flex-wrap gap-2">
            {PARTY_SCORE_OPTIONS.map((o) => {
              const selected = partyScoreValues.includes(o.value);
              return (
                <label
                  key={o.value}
                  className={`inline-flex items-center gap-2 px-2.5 py-1.5 rounded border cursor-pointer text-xs ${
                    selected
                      ? 'border-accent bg-accent/15 text-fg-primary'
                      : 'border-border-base bg-bg-base text-fg-secondary hover:bg-bg-elevated'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selected}
                    onChange={() => {
                      if (selected) {
                        onPartyScoreValuesChange(
                          partyScoreValues.filter((v) => v !== o.value),
                        );
                      } else {
                        onPartyScoreValuesChange([
                          ...partyScoreValues,
                          o.value,
                        ]);
                      }
                    }}
                    className="accent-emerald-500"
                  />
                  {o.label}
                </label>
              );
            })}
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-2 max-w-xl">
        <span className="text-[10px] uppercase tracking-wide text-fg-muted font-semibold">
          Limit to first N voters
        </span>
        <div className="flex items-center gap-3">
          <input
            type="range"
            min={1}
            max={5000}
            step={1}
            value={audienceLimit}
            onChange={(e) =>
              onAudienceLimitChange(Number.parseInt(e.target.value, 10))
            }
            className="flex-1 accent-emerald-500"
          />
          <input
            type="number"
            min={1}
            max={5000}
            value={audienceLimit}
            onChange={(e) => {
              const v = Number.parseInt(e.target.value, 10);
              if (!Number.isFinite(v)) return;
              onAudienceLimitChange(Math.max(1, Math.min(5000, v)));
            }}
            className="w-24 bg-bg-base border border-border-base rounded px-2 py-1.5 text-fg-primary text-sm tabular-nums"
          />
        </div>
      </div>

      {mode === 'batch' && (
        <div className="text-xs text-fg-secondary bg-status-info-bg/20 border border-status-info-bg/40 rounded p-3 inline-flex items-start gap-2">
          <Users
            className="w-4 h-4 flex-shrink-0 mt-0.5 text-status-info"
            aria-hidden="true"
          />
          <span>
            Batch sends only go to voters who have explicitly opted in via
            doorknock or web form. Voters without an opt-in are excluded
            automatically.
          </span>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 4 — Preview
// ---------------------------------------------------------------------------
function StepPreview({
  loading,
  error,
  preview,
  onEditBasics,
  onEditTemplate,
  onEditAudience,
}: {
  loading: boolean;
  error: string | null;
  preview: BatchPreviewResponse | null;
  onEditBasics: () => void;
  onEditTemplate: () => void;
  onEditAudience: () => void;
}) {
  if (loading) {
    return (
      <div className="space-y-3">
        <h2 className="text-lg font-bold text-fg-primary">Preview</h2>
        <div className="text-sm text-fg-muted inline-flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
          Resolving audience…
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="space-y-3">
        <h2 className="text-lg font-bold text-fg-primary">Preview</h2>
        <div className="rounded p-3 border border-status-danger-bg bg-status-danger-bg/30 text-status-danger text-sm">
          {error}
        </div>
      </div>
    );
  }
  if (!preview) {
    return (
      <div className="space-y-3">
        <h2 className="text-lg font-bold text-fg-primary">Preview</h2>
        <div className="text-sm text-fg-muted">No preview available yet.</div>
      </div>
    );
  }
  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="text-lg font-bold text-fg-primary">Preview</h2>
        <div className="flex items-center gap-2 flex-wrap">
          <EditPill onClick={onEditBasics} label="Edit basics" />
          <EditPill onClick={onEditTemplate} label="Edit template" />
          <EditPill onClick={onEditAudience} label="Edit audience" />
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <PreviewStat
          label="Total in audience"
          value={preview.total_in_audience}
          tone="neutral"
        />
        <PreviewStat
          label="Eligible to send"
          value={preview.eligible_to_send}
          tone="success"
          big
        />
        <PreviewStat
          label="Excluded — no phone"
          value={preview.excluded_no_phone}
          tone="muted"
        />
        <PreviewStat
          label="Excluded — opted out"
          value={preview.excluded_do_not_text}
          tone="muted"
        />
        <PreviewStat
          label="Excluded — no opt-in"
          value={preview.excluded_no_opt_in}
          tone="muted"
        />
      </div>

      {preview.eligible_to_send === 0 && (
        <div className="rounded p-3 border border-status-danger-bg bg-status-danger-bg/30 text-status-danger text-sm">
          No voters in this audience are eligible to receive batch SMS. Try
          a different audience or broaden the precinct / party filters.
        </div>
      )}

      <div>
        <div className="text-xs uppercase tracking-wide text-fg-muted font-semibold mb-2">
          Sample recipients (first 5)
        </div>
        {preview.sample_recipients.length === 0 ? (
          <div className="text-sm text-fg-muted">No sample available.</div>
        ) : (
          <ul className="text-sm text-fg-secondary space-y-1">
            {preview.sample_recipients.slice(0, 5).map((s) => (
              <li
                key={s.voter_id}
                className="inline-flex items-center gap-2 mr-3"
              >
                <CheckCircle2
                  className="w-3.5 h-3.5 text-status-success"
                  aria-hidden="true"
                />
                {s.first_name} {s.last_name}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function EditPill({
  label,
  onClick,
}: {
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-xs px-2 py-1 rounded border border-border-base bg-bg-base hover:bg-bg-elevated text-fg-secondary hover:text-fg-primary"
    >
      {label}
    </button>
  );
}

function PreviewStat({
  label,
  value,
  tone,
  big,
}: {
  label: string;
  value: number;
  tone: 'success' | 'neutral' | 'muted';
  big?: boolean;
}) {
  const palette =
    tone === 'success'
      ? 'bg-status-success-bg/25 border-status-success-bg/50 text-status-success'
      : tone === 'neutral'
        ? 'bg-bg-base/40 border-border-base text-fg-primary'
        : 'bg-bg-base/40 border-border-base text-fg-muted';
  return (
    <div className={`rounded-lg border px-3 py-3 ${palette}`}>
      <div className="text-[10px] uppercase tracking-wider">{label}</div>
      <div
        className={`mt-0.5 font-bold text-fg-primary tabular-nums ${big ? 'text-4xl' : 'text-2xl'}`}
      >
        {value.toLocaleString()}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 5 — Attest + Launch
// ---------------------------------------------------------------------------
function StepAttest({
  orgName,
  consentAttested,
  onConsentChange,
  launching,
  error,
  onLaunch: _onLaunch,
  eligibleCount,
  scheduleKind,
  scheduledAt,
  mode,
}: {
  orgName: string;
  consentAttested: boolean;
  onConsentChange: (v: boolean) => void;
  launching: boolean;
  error: string | null;
  onLaunch: () => void;
  eligibleCount: number;
  scheduleKind: 'now' | 'later';
  scheduledAt: string;
  mode: VoterCampaignMode;
}) {
  return (
    <div className="space-y-5">
      <h2 className="text-xl font-extrabold text-fg-primary">
        Confirm audience consent
      </h2>

      <p className="text-sm text-fg-secondary leading-relaxed">
        Under TCPA, batch SMS to consumers requires prior express written
        consent. By checking the box below, you affirm that every voter in
        the eligible audience has previously opted in to text-message
        contact from {orgName}.
      </p>

      <div className="rounded-lg border border-accent/40 bg-accent/10 p-4">
        <div className="text-sm text-fg-secondary">
          You&apos;re about to send{' '}
          <span className="font-bold text-fg-primary tabular-nums">
            {eligibleCount.toLocaleString()}
          </span>{' '}
          messages
          {mode === 'p2p' && (
            <span className="text-status-warning">
              {' '}
              (P2P mode — recipients will be queued for click-to-send,
              not auto-sent)
            </span>
          )}
          {scheduleKind === 'now'
            ? ' immediately on launch.'
            : ` at ${
                scheduledAt
                  ? new Date(scheduledAt).toLocaleString()
                  : 'the scheduled time'
              }.`}
        </div>
      </div>

      <label className="inline-flex items-start gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={consentAttested}
          onChange={(e) => onConsentChange(e.target.checked)}
          disabled={launching}
          className="accent-emerald-500 w-5 h-5 mt-0.5"
        />
        <span className="text-sm font-semibold text-fg-primary">
          I confirm the audience has prior express consent.
        </span>
      </label>

      {error && (
        <div className="rounded p-3 border border-status-danger-bg bg-status-danger-bg/30 text-status-danger text-sm">
          {error}
        </div>
      )}
    </div>
  );
}

export default CampaignComposerView;
